import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';

interface UsernameDialogProps {
  open: boolean;
  onClose: () => void;
  currentUsername: string;
  onUsernameChange: (username: string) => void;
}

export function UsernameDialog({ open, onClose, currentUsername, onUsernameChange }: UsernameDialogProps) {
  const { t } = useTranslation();
  const [username, setUsername] = useState(currentUsername);
  const [error, setError] = useState('');

  useEffect(() => {
    setUsername(currentUsername);
  }, [currentUsername]);

  const handleSave = () => {
    const trimmedUsername = username.trim();
    
    if (!trimmedUsername) {
      setError(t('Username cannot be empty'));
      return;
    }
    
    if (trimmedUsername.length < 2) {
      setError(t('Username must be at least 2 characters'));
      return;
    }
    
    if (trimmedUsername.length > 30) {
      setError(t('Username must be less than 30 characters'));
      return;
    }
    
    onUsernameChange(trimmedUsername);
    setError('');
    onClose();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('Username Settings')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {t('This name will be shown to other users when you create geometries')}
        </Typography>
        <TextField
          autoFocus
          margin="dense"
          label={t('Enter your username')}
          fullWidth
          value={username}
          onChange={(e) => {
            setUsername(e.target.value);
            setError('');
          }}
          onKeyPress={handleKeyPress}
          error={!!error}
          helperText={error}
          inputProps={{ maxLength: 30 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Cancel')}</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          {t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}